
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Filter, X } from "lucide-react";
import { EtsyOrder } from "@/types/etsy";

interface FilterOptions {
  statuses: string[];
  dateFrom: string;
  dateTo: string;
  minAmount: string;
  maxAmount: string;
}

interface OrdersFilterDialogProps {
  orders: EtsyOrder[];
  onFiltersChange: (filteredOrders: EtsyOrder[]) => void;
  children: React.ReactNode;
}

const OrdersFilterDialog = ({ orders, onFiltersChange, children }: OrdersFilterDialogProps) => {
  const [filters, setFilters] = useState<FilterOptions>({
    statuses: [],
    dateFrom: "",
    dateTo: "",
    minAmount: "",
    maxAmount: ""
  });
  const [isOpen, setIsOpen] = useState(false);

  // Get unique statuses from orders
  const uniqueStatuses = Array.from(new Set(orders.map(order => order.status)));

  const handleStatusChange = (status: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      statuses: checked 
        ? [...prev.statuses, status]
        : prev.statuses.filter(s => s !== status)
    }));
  };

  const applyFilters = () => {
    let filteredOrders = [...orders];

    // Filter by status
    if (filters.statuses.length > 0) {
      filteredOrders = filteredOrders.filter(order => 
        filters.statuses.includes(order.status)
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filteredOrders = filteredOrders.filter(order => 
        new Date(order.date) >= fromDate
      );
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      filteredOrders = filteredOrders.filter(order => 
        new Date(order.date) <= toDate
      );
    }

    // Filter by amount range
    if (filters.minAmount) {
      const minAmount = parseFloat(filters.minAmount);
      filteredOrders = filteredOrders.filter(order => 
        (order.totalAmount || 0) >= minAmount
      );
    }

    if (filters.maxAmount) {
      const maxAmount = parseFloat(filters.maxAmount);
      filteredOrders = filteredOrders.filter(order => 
        (order.totalAmount || 0) <= maxAmount
      );
    }

    onFiltersChange(filteredOrders);
    setIsOpen(false);
  };

  const clearFilters = () => {
    setFilters({
      statuses: [],
      dateFrom: "",
      dateTo: "",
      minAmount: "",
      maxAmount: ""
    });
    onFiltersChange(orders);
  };

  const hasActiveFilters = filters.statuses.length > 0 || 
    filters.dateFrom || filters.dateTo || filters.minAmount || filters.maxAmount;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Filter Orders</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Status Filter */}
          <div>
            <Label className="text-sm font-medium">Order Status</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {uniqueStatuses.map(status => (
                <div key={status} className="flex items-center space-x-2">
                  <Checkbox
                    id={`status-${status}`}
                    checked={filters.statuses.includes(status)}
                    onCheckedChange={(checked) => handleStatusChange(status, checked as boolean)}
                  />
                  <Label htmlFor={`status-${status}`} className="text-sm capitalize">
                    {status}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div>
            <Label className="text-sm font-medium">Date Range</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label htmlFor="dateFrom" className="text-xs text-gray-600">From</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="dateTo" className="text-xs text-gray-600">To</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Amount Range Filter */}
          <div>
            <Label className="text-sm font-medium">Amount Range</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label htmlFor="minAmount" className="text-xs text-gray-600">Min Amount</Label>
                <Input
                  id="minAmount"
                  type="number"
                  placeholder="0.00"
                  value={filters.minAmount}
                  onChange={(e) => setFilters(prev => ({ ...prev, minAmount: e.target.value }))}
                />
              </div>
              <div>
                <Label htmlFor="maxAmount" className="text-xs text-gray-600">Max Amount</Label>
                <Input
                  id="maxAmount"
                  type="number"
                  placeholder="999.99"
                  value={filters.maxAmount}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxAmount: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4">
            <Button onClick={applyFilters} className="flex-1">
              Apply Filters
            </Button>
            {hasActiveFilters && (
              <Button onClick={clearFilters} variant="outline" size="icon">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrdersFilterDialog;
